/**
 * WhatsApp ID utility functions
 * Provides consistent handling of WhatsApp IDs across the codebase
 *
 * WhatsApp IDs follow the format: phone_number@server
 * Examples:
 * - Individual chat: "1234567890@c.us"
 * - Group chat: "123456789-123456789@g.us"
 * - Status: "status@broadcast"
 */

/**
 * Extracts the phone number from a WhatsApp ID
 *
 * @param whatsappId - The WhatsApp ID (e.g., "1234567890@c.us")
 * @returns The phone number portion, or empty string if invalid
 *
 * @example
 * extractPhoneNumber("1234567890@c.us") // returns "1234567890"
 * extractPhoneNumber("1234567890") // returns "1234567890"
 * extractPhoneNumber(undefined) // returns ""
 */
export function extractPhoneNumber(whatsappId: string | undefined | null): string {
    if (!whatsappId) return '';
    return whatsappId.split('@')[0] || '';
}

/**
 * Extracts the phone number with optional fallback
 *
 * @param whatsappId - The WhatsApp ID
 * @param fallback - Value to return if extraction fails (defaults to 'Unknown')
 * @returns The phone number or fallback value
 */
export function extractPhoneNumberOrFallback(
    whatsappId: string | undefined | null,
    fallback: string = 'Unknown'
): string {
    const phone = extractPhoneNumber(whatsappId);
    return phone || fallback;
}

/**
 * Extracts the server portion from a WhatsApp ID
 *
 * @param whatsappId - The WhatsApp ID (e.g., "1234567890@c.us")
 * @returns The server portion (e.g., "c.us"), or empty string if invalid
 */
export function extractServer(whatsappId: string | undefined | null): string {
    if (!whatsappId) return '';
    const parts = whatsappId.split('@');
    return parts[1] || '';
}

/**
 * Parses a WhatsApp ID into its components
 *
 * @param whatsappId - The WhatsApp ID to parse
 * @returns Object with user and server portions
 */
export function parseWhatsappId(whatsappId: string | undefined | null): { user: string; server: string } {
    if (!whatsappId) return { user: '', server: '' };
    const [user = '', server = ''] = whatsappId.split('@');
    return { user, server };
}

/**
 * Checks if a WhatsApp ID is an individual chat
 *
 * @param whatsappId - The WhatsApp ID to check
 * @returns True if it's an individual chat ID
 */
export function isIndividualChat(whatsappId: string | undefined | null): boolean {
    return extractServer(whatsappId) === 'c.us';
}

/**
 * Creates a WhatsApp ID from a phone number
 *
 * @param phoneNumber - The phone number (digits only)
 * @param isGroup - Whether to create a group ID (defaults to false)
 * @returns The formatted WhatsApp ID
 */
export function createWhatsappId(phoneNumber: string, isGroup: boolean = false): string {
    const server = isGroup ? 'g.us' : 'c.us';
    return `${phoneNumber}@${server}`;
}

/**
 * Cleans a phone number by removing non-digit characters except @
 * and then extracts the phone portion
 *
 * @param phone - The phone number or WhatsApp ID to clean
 * @returns Cleaned phone number (digits only)
 */
export function cleanPhoneNumber(phone: string | undefined | null): string {
    if (!phone) return '';
    return phone.replace(/[^\d@]/g, '').split('@')[0] || '';
}
